import type { NormalizedProductVariant } from "@cappelendamm/catalogue/crystallize/product-translator.js";

type ContributorProduct = Extract<
  NormalizedProductVariant,
  { itemType: "book" | "digital-service" }
>;

export type ContributorProductFields = Pick<
  ContributorProduct,
  "sku" | "itemType" | "canBeVisible"
> & {
  shops?:
    Pick<NonNullable<ContributorProduct["shops"]>[number], "path">[] | null;
  itemDetails: Pick<ContributorProduct["itemDetails"], "contributors">;
};

export const CONTRIBUTOR_PRODUCT_BATCH_SIZE = 20;

export function getContributorProductBatch(skus: string[], offset: number) {
  const batch = skus.slice(offset, offset + CONTRIBUTOR_PRODUCT_BATCH_SIZE);
  const nextOffset = offset + batch.length;
  return {
    skus: batch,
    nextOffset: nextOffset < skus.length ? nextOffset : null,
  };
}

export function selectBatchContributors(
  products: ContributorProductFields[],
  skus: string[],
  currentShop: string,
) {
  const bySku = new Map(products.map((product) => [product.sku, product]));
  return skus.flatMap((sku) => {
    const product = bySku.get(sku);
    if (
      !product ||
      !["book", "digital-service"].includes(product.itemType) ||
      !product.canBeVisible ||
      !product.shops?.some((shop) => shop.path === currentShop)
    ) {
      return [];
    }
    return product.itemDetails.contributors;
  });
}

export function mergeContributors<C extends { scid: number }>(
  previous: C[],
  incoming: C[],
): C[] {
  const seen = new Set<number>();
  return [...previous, ...incoming].filter((contributor) => {
    if (!contributor.scid || seen.has(contributor.scid)) return false;
    seen.add(contributor.scid);
    return true;
  });
}
